import { useMemo, useState, type ReactNode } from 'react'
import {
  ArrowDown,
  ArrowUp,
  ArrowsDownUp,
  CaretLeft,
  CaretRight,
  MagnifyingGlass,
} from '@phosphor-icons/react'
import { cn } from '@/lib/cn'
import { useI18n } from '@/i18n'
import { Input } from './Input'
import { Skeleton } from './Skeleton'
import { EmptyState } from './EmptyState'
import { Banner } from './Banner'

export interface Column<T> {
  id: string
  header: ReactNode
  cell: (row: T) => ReactNode
  /** Provide to make the column sortable (client mode) or to mark it sortable (server mode). */
  sortValue?: (row: T) => string | number | null | undefined
  align?: 'left' | 'right' | 'center'
  /** CSS width, e.g. '120px' or '20%'. */
  width?: string
  /** Hide on narrow screens. */
  hideBelow?: 'sm' | 'md' | 'lg'
  /** Monospace + tabular figures (codes, kg, ₹). */
  mono?: boolean
}

export type SortDir = 'asc' | 'desc'
export interface SortState {
  id: string
  dir: SortDir
}

export interface DataTableProps<T> {
  columns: ReadonlyArray<Column<T>>
  rows: ReadonlyArray<T>
  getRowId: (row: T) => string
  /** Accessible table name. */
  caption: string
  loading?: boolean
  error?: ReactNode
  /** Shown when there are no rows (and no search). */
  empty?: ReactNode
  onRowClick?: (row: T) => void
  /** Filter controls rendered left of the search box (FilterBar children). */
  filters?: ReactNode
  /** Client-side search across `searchText(row)`. Omit to hide the search box. */
  searchText?: (row: T) => string
  searchPlaceholder?: string
  initialSort?: SortState
  pageSize?: number
  /**
   * Server mode: rows are the current page; the parent fetches on change.
   * When set, client sorting/search/pagination are off.
   */
  server?: {
    page: number
    total: number
    onPageChange: (page: number) => void
    sort?: SortState | null
    onSortChange?: (sort: SortState | null) => void
  }
  className?: string
}

const hide = {
  sm: 'hidden sm:table-cell',
  md: 'hidden md:table-cell',
  lg: 'hidden lg:table-cell',
} as const
const alignCls = { left: 'text-left', right: 'text-right', center: 'text-center' } as const

function compare(
  a: string | number | null | undefined,
  b: string | number | null | undefined,
): number {
  if (a == null && b == null) return 0
  if (a == null) return 1
  if (b == null) return -1
  if (typeof a === 'number' && typeof b === 'number') return a - b
  return String(a).localeCompare(String(b), 'en-IN', { numeric: true, sensitivity: 'base' })
}

/**
 * Console table: sticky header, sortable columns, search + filter bar, pagination, loading
 * skeleton, empty and error states. Client-side by default; pass `server` for API paging.
 */
export function DataTable<T>({
  columns,
  rows,
  getRowId,
  caption,
  loading,
  error,
  empty,
  onRowClick,
  filters,
  searchText,
  searchPlaceholder,
  initialSort,
  pageSize = 25,
  server,
  className,
}: DataTableProps<T>) {
  const { t } = useI18n()
  const [clientSort, setClientSort] = useState<SortState | null>(initialSort ?? null)
  const [clientPage, setClientPage] = useState(1)
  const [q, setQ] = useState('')

  const sort = server ? (server.sort ?? null) : clientSort
  const setSort = (s: SortState | null) => (server ? server.onSortChange?.(s) : setClientSort(s))

  const processed = useMemo(() => {
    if (server) return rows
    let list = rows
    const needle = q.trim().toLowerCase()
    if (needle && searchText)
      list = list.filter((r) => searchText(r).toLowerCase().includes(needle))
    const col = sort && columns.find((c) => c.id === sort.id)
    if (col?.sortValue) {
      const get = col.sortValue
      list = [...list].sort((a, b) => compare(get(a), get(b)) * (sort!.dir === 'asc' ? 1 : -1))
    }
    return list
  }, [rows, server, q, searchText, sort, columns])

  const total = server ? server.total : processed.length
  const pages = Math.max(1, Math.ceil(total / pageSize))
  const page = Math.min(server ? server.page : clientPage, pages)
  const setPage = (p: number) => (server ? server.onPageChange(p) : setClientPage(p))
  const visible = server ? processed : processed.slice((page - 1) * pageSize, page * pageSize)

  const toggleSort = (id: string) => {
    if (!sort || sort.id !== id) setSort({ id, dir: 'asc' })
    else if (sort.dir === 'asc') setSort({ id, dir: 'desc' })
    else setSort(null)
    if (!server) setClientPage(1)
  }

  const showToolbar = Boolean(filters || searchText)

  return (
    <div
      className={cn('flex min-w-0 flex-col rounded-card border border-rule bg-surface', className)}
    >
      {showToolbar && (
        <div className="flex flex-wrap items-center gap-2 border-b border-rule px-3 py-2.5">
          {filters && <div className="flex flex-wrap items-center gap-2">{filters}</div>}
          {searchText && !server && (
            <div className="ml-auto w-full sm:w-64">
              <Input
                type="search"
                value={q}
                onChange={(e) => {
                  setQ(e.target.value)
                  setClientPage(1)
                }}
                placeholder={searchPlaceholder ?? t('common.search')}
                aria-label={searchPlaceholder ?? t('common.search')}
                leading={<MagnifyingGlass />}
              />
            </div>
          )}
        </div>
      )}

      {error ? (
        <div className="p-3">
          <Banner tone="error">{error}</Banner>
        </div>
      ) : (
        <div className="max-h-[70vh] min-w-0 overflow-auto">
          <table className="w-full border-separate border-spacing-0 text-[14px]">
            <caption className="sr-only">{caption}</caption>
            <thead>
              <tr>
                {columns.map((c) => {
                  const sorted = sort?.id === c.id ? sort.dir : null
                  return (
                    <th
                      key={c.id}
                      scope="col"
                      style={c.width ? { width: c.width } : undefined}
                      aria-sort={
                        sorted === 'asc'
                          ? 'ascending'
                          : sorted === 'desc'
                            ? 'descending'
                            : undefined
                      }
                      className={cn(
                        'sticky top-0 z-10 whitespace-nowrap border-b border-rule bg-paper px-3 py-2.5 font-mono text-[10.5px] font-bold uppercase tracking-eyebrow text-muted',
                        alignCls[c.align ?? 'left'],
                        c.hideBelow && hide[c.hideBelow],
                      )}
                    >
                      {c.sortValue ? (
                        <button
                          type="button"
                          onClick={() => toggleSort(c.id)}
                          className={cn(
                            'inline-flex items-center gap-1 uppercase hover:text-ink',
                            sorted && 'text-ink',
                          )}
                        >
                          {c.header}
                          {sorted === 'asc' ? (
                            <ArrowUp aria-hidden="true" />
                          ) : sorted === 'desc' ? (
                            <ArrowDown aria-hidden="true" />
                          ) : (
                            <ArrowsDownUp aria-hidden="true" className="opacity-40" />
                          )}
                        </button>
                      ) : (
                        c.header
                      )}
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {loading
                ? Array.from({ length: 6 }, (_, i) => (
                    <tr key={`sk${i}`}>
                      {columns.map((c) => (
                        <td
                          key={c.id}
                          className={cn(
                            'border-b border-rule/70 px-3 py-3',
                            c.hideBelow && hide[c.hideBelow],
                          )}
                        >
                          <Skeleton className="h-3.5 w-3/4" />
                        </td>
                      ))}
                    </tr>
                  ))
                : visible.map((row) => (
                    <tr
                      key={getRowId(row)}
                      onClick={onRowClick ? () => onRowClick(row) : undefined}
                      onKeyDown={
                        onRowClick
                          ? (e) => {
                              if (e.key === 'Enter') onRowClick(row)
                            }
                          : undefined
                      }
                      tabIndex={onRowClick ? 0 : undefined}
                      className={cn(
                        'group',
                        onRowClick &&
                          'cursor-pointer hover:bg-paper-tint/60 focus-visible:bg-paper-tint',
                      )}
                    >
                      {columns.map((c) => (
                        <td
                          key={c.id}
                          className={cn(
                            'border-b border-rule/70 px-3 py-2.5 align-middle text-ink group-last:border-b-0',
                            alignCls[c.align ?? 'left'],
                            c.mono && 'font-mono tabular text-[13px]',
                            c.hideBelow && hide[c.hideBelow],
                          )}
                        >
                          {c.cell(row)}
                        </td>
                      ))}
                    </tr>
                  ))}
            </tbody>
          </table>
          {!loading &&
            visible.length === 0 &&
            (q ? <EmptyState compact title={t('common.noResults')} /> : empty)}
        </div>
      )}

      {!error && total > 0 && (
        <div className="flex items-center justify-between gap-3 border-t border-rule px-3 py-2 text-[13px] text-muted">
          <span className="font-mono tabular">{t('common.rows', { count: total })}</span>
          {pages > 1 && (
            <div className="flex items-center gap-1">
              <button
                type="button"
                className="inline-flex h-8 w-8 items-center justify-center rounded-chip hover:bg-ink/5 disabled:opacity-30"
                onClick={() => setPage(page - 1)}
                disabled={page <= 1}
                aria-label={t('common.previous')}
              >
                <CaretLeft aria-hidden="true" />
              </button>
              <span className="px-1 font-mono tabular">{t('common.page', { page, pages })}</span>
              <button
                type="button"
                className="inline-flex h-8 w-8 items-center justify-center rounded-chip hover:bg-ink/5 disabled:opacity-30"
                onClick={() => setPage(page + 1)}
                disabled={page >= pages}
                aria-label={t('common.next')}
              >
                <CaretRight aria-hidden="true" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/** A filter "chip" toggle for the DataTable filter bar. */
export function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'h-8 rounded-chip border px-2.5 font-mono text-[11px] font-bold uppercase tracking-label transition-colors',
        active
          ? 'border-ink bg-ink text-paper'
          : 'border-rule bg-surface text-ink-soft hover:border-ink/40',
      )}
    >
      {children}
    </button>
  )
}
